import { CalendarOutlined, CommentOutlined } from '@ant-design/icons';
import { AudioProvider, PrimaryButton, SecondaryButton, styleVariables, TextLinkButton } from '@lib/common';
import { SectionV2Preview } from '@module/form-components';
import { Progress, Skeleton, Space } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { CommentBox, CommentPreview } from '../comment';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var SubmissionPreviewLayout = styled.div.withConfig({
  displayName: "practice-submission-result-previewcomponent__SubmissionPreviewLayout",
  componentId: "sc-4vzrbq-0"
})(["display:flex;justify-content:center;height:100%;width:100%;overflow-y:auto;scroll-behavior:smooth;&::-webkit-scrollbar{-webkit-appearance:none;}"]);
var Content = styled(Space).withConfig({
  displayName: "practice-submission-result-previewcomponent__Content",
  componentId: "sc-4vzrbq-1"
})(["width:100%;width:min(75%,", "px);padding-top:16px;@media (max-width:768px){padding-top:12px;}> div.ant-space-item:last-child{padding-bottom:50px;}"], styleVariables.responsive.largeScreen);
var UnifiedCard = styled.div.withConfig({
  displayName: "practice-submission-result-previewcomponent__UnifiedCard",
  componentId: "sc-4vzrbq-2"
})(["width:100%;border-radius:12px;background:", ";box-shadow:0 1px 3px rgba(15,23,42,0.08);border:1px solid #e2e8f0;overflow:hidden;"], styleVariables.colors.gray1);
var AssessmentSection = styled.div.withConfig({
  displayName: "practice-submission-result-previewcomponent__AssessmentSection",
  componentId: "sc-4vzrbq-3"
})(["display:flex;flex-wrap:wrap;align-items:center;gap:20px 24px;padding:20px 24px;"]);
var SectionDivider = styled.div.withConfig({
  displayName: "practice-submission-result-previewcomponent__SectionDivider",
  componentId: "sc-4vzrbq-4"
})(["height:1px;background:#e2e8f0;margin:0 24px;"]);
var ScoreRingWrapper = styled.div.withConfig({
  displayName: "practice-submission-result-previewcomponent__ScoreRingWrapper",
  componentId: "sc-4vzrbq-5"
})(["display:flex;flex-direction:column;align-items:center;flex-shrink:0;"]);
var GlobalLabel = styled.span.withConfig({
  displayName: "practice-submission-result-previewcomponent__GlobalLabel",
  componentId: "sc-4vzrbq-6"
})(["margin-top:4px;font-size:10px;font-weight:700;letter-spacing:0.1em;color:#94a3b8;text-transform:uppercase;"]);
var AssessmentTextBlock = styled.div.withConfig({
  displayName: "practice-submission-result-previewcomponent__AssessmentTextBlock",
  componentId: "sc-4vzrbq-7"
})(["flex:1;min-width:200px;"]);
var AssessmentTitle = styled.h2.withConfig({
  displayName: "practice-submission-result-previewcomponent__AssessmentTitle",
  componentId: "sc-4vzrbq-8"
})(["margin:0 0 4px;font-size:20px;font-weight:700;line-height:1.3;color:#0f172a;"]);
var AssessmentSubtitle = styled.p.withConfig({
  displayName: "practice-submission-result-previewcomponent__AssessmentSubtitle",
  componentId: "sc-4vzrbq-9"
})(["margin:0;font-size:14px;font-weight:500;line-height:1.4;color:#64748b;"]);
var StatBadges = styled.div.withConfig({
  displayName: "practice-submission-result-previewcomponent__StatBadges",
  componentId: "sc-4vzrbq-10"
})(["display:flex;flex-wrap:wrap;gap:10px;margin-left:auto;"]);
var StatBadge = styled.div.withConfig({
  displayName: "practice-submission-result-previewcomponent__StatBadge",
  componentId: "sc-4vzrbq-11"
})(["min-width:88px;padding:10px 14px;border-radius:8px;text-align:center;background:", ";"], function (_ref) {
  var $variant = _ref.$variant;
  return $variant === 'score' ? '#ecfdf5' : '#eff6ff';
});
var StatValue = styled.div.withConfig({
  displayName: "practice-submission-result-previewcomponent__StatValue",
  componentId: "sc-4vzrbq-12"
})(["font-size:18px;font-weight:700;line-height:1.2;color:", ";"], function (_ref2) {
  var $variant = _ref2.$variant;
  return $variant === 'score' ? '#16a34a' : '#2563eb';
});
var StatLabel = styled.div.withConfig({
  displayName: "practice-submission-result-previewcomponent__StatLabel",
  componentId: "sc-4vzrbq-13"
})(["margin-top:2px;font-size:10px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;color:", ";"], function (_ref3) {
  var $variant = _ref3.$variant;
  return $variant === 'score' ? '#16a34a' : '#2563eb';
});
var UserSection = styled.div.withConfig({
  displayName: "practice-submission-result-previewcomponent__UserSection",
  componentId: "sc-4vzrbq-14"
})(["display:flex;flex-wrap:wrap;align-items:center;gap:16px 24px;padding:18px 24px;"]);
var AvatarCircle = styled.div.withConfig({
  displayName: "practice-submission-result-previewcomponent__AvatarCircle",
  componentId: "sc-4vzrbq-15"
})(["width:52px;height:52px;border-radius:50%;display:flex;align-items:center;justify-content:center;flex-shrink:0;background:#2563eb;color:#ffffff;font-weight:700;font-size:16px;"]);
var UserDetails = styled.div.withConfig({
  displayName: "practice-submission-result-previewcomponent__UserDetails",
  componentId: "sc-4vzrbq-16"
})(["flex:1;min-width:180px;"]);
var UserName = styled.div.withConfig({
  displayName: "practice-submission-result-previewcomponent__UserName",
  componentId: "sc-4vzrbq-17"
})(["font-size:16px;font-weight:700;color:#0f172a;line-height:1.3;"]);
var UserEmail = styled.div.withConfig({
  displayName: "practice-submission-result-previewcomponent__UserEmail",
  componentId: "sc-4vzrbq-18"
})(["margin-top:2px;font-size:14px;font-weight:500;color:#64748b;"]);
var TestDateBlock = styled.div.withConfig({
  displayName: "practice-submission-result-previewcomponent__TestDateBlock",
  componentId: "sc-4vzrbq-19"
})(["display:flex;flex-direction:column;align-items:flex-end;gap:6px;min-width:200px;margin-left:auto;"]);
var TestDateLabel = styled.span.withConfig({
  displayName: "practice-submission-result-previewcomponent__TestDateLabel",
  componentId: "sc-4vzrbq-20"
})(["font-size:10px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;color:#94a3b8;"]);
var TestDateValue = styled.div.withConfig({
  displayName: "practice-submission-result-previewcomponent__TestDateValue",
  componentId: "sc-4vzrbq-21"
})(["display:inline-flex;align-items:center;gap:8px;padding:8px 12px;border-radius:8px;background:#f8fafc;border:1px solid #e2e8f0;font-size:13px;font-weight:600;color:#334155;"]);
var FeedbackSection = styled.div.withConfig({
  displayName: "practice-submission-result-previewcomponent__FeedbackSection",
  componentId: "sc-4vzrbq-22"
})(["padding:18px 20px 18px 16px;background:#fffbeb;border-top:1px solid #fde68a;border-left:4px solid #f97316;"]);
var FeedbackHeader = styled.div.withConfig({
  displayName: "practice-submission-result-previewcomponent__FeedbackHeader",
  componentId: "sc-4vzrbq-23"
})(["display:flex;align-items:center;gap:8px;margin-bottom:10px;font-size:15px;font-weight:700;color:#0f172a;"]);
var FeedbackIcon = styled(CommentOutlined).withConfig({
  displayName: "practice-submission-result-previewcomponent__FeedbackIcon",
  componentId: "sc-4vzrbq-24"
})(["font-size:18px;color:#f97316;"]);
var FeedbackBody = styled.div.withConfig({
  displayName: "practice-submission-result-previewcomponent__FeedbackBody",
  componentId: "sc-4vzrbq-25"
})(["font-size:14px;line-height:1.55;color:#64748b;font-style:italic;"]);
var AccuracyProgress = styled(Progress).withConfig({
  displayName: "practice-submission-result-previewcomponent__AccuracyProgress",
  componentId: "sc-4vzrbq-26"
})([".ant-progress-inner{width:120px !important;height:120px !important;}.ant-progress-text{display:none;}"]);
var ProgressRingHost = styled.div.withConfig({
  displayName: "practice-submission-result-previewcomponent__ProgressRingHost",
  componentId: "sc-4vzrbq-27"
})(["position:relative;width:120px;height:120px;"]);
var ProgressCenter = styled.div.withConfig({
  displayName: "practice-submission-result-previewcomponent__ProgressCenter",
  componentId: "sc-4vzrbq-28"
})(["position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;pointer-events:none;"]);
var ProgressCenterValue = styled.span.withConfig({
  displayName: "practice-submission-result-previewcomponent__ProgressCenterValue",
  componentId: "sc-4vzrbq-29"
})(["font-size:28px;font-weight:700;line-height:1;color:", ";"], function (_ref4) {
  var $hasPassed = _ref4.$hasPassed;
  return $hasPassed ? '#2563eb' : '#dc2626';
});
var StyledSectionContainer = styled.div.withConfig({
  displayName: "practice-submission-result-previewcomponent__StyledSectionContainer",
  componentId: "sc-4vzrbq-30"
})(["display:flex;flex-direction:column;gap:16px;width:100%;"]);
var StyledButtonGroup = styled.div.withConfig({
  displayName: "practice-submission-result-previewcomponent__StyledButtonGroup",
  componentId: "sc-4vzrbq-31"
})(["display:flex;justify-content:space-between;padding:24px 0 50px;width:100%;"]);

var toDisplayScore = function toDisplayScore(score) {
  return typeof score === 'number' && Number.isFinite(score) && score > 0 ? score : 0;
};

var getMetrics = function getMetrics(props) {
  var _assignment$expectati, _ref5, _assignment$title;

  var assignment = props.assignment,
      formRelease = props.formRelease,
      submission = props.submission,
      questionReleases = props.questionReleases,
      answers = props.answers;

  if (!assignment || !formRelease || !submission || !questionReleases) {
    return undefined;
  }

  var scoreSummary = submission.getScoreSummary({
    formRelease: formRelease,
    submissionAnswers: answers !== null && answers !== void 0 ? answers : []
  });
  var totalScore = scoreSummary.totalScore;
  var earnedScore = toDisplayScore(scoreSummary.earnedScore);
  var globalPercent = Math.max(0, Math.min(100, Math.round(scoreSummary.globalPercent)));
  var expectation = (_assignment$expectati = assignment.expectation) !== null && _assignment$expectati !== void 0 ? _assignment$expectati : 0;
  var passingPercentage = assignment.expectationUnit === 'score' && totalScore > 0 ? Math.round(expectation / totalScore * 100) : expectation;
  return {
    globalPercent: globalPercent,
    earnedScore: earnedScore,
    totalScore: totalScore,
    timeTakenMinutes: submission.getTimeTakenMinutes({
      assignmentDuration: assignment.duration
    }),
    assignmentTitle: (_ref5 = (_assignment$title = assignment.title) !== null && _assignment$title !== void 0 ? _assignment$title : formRelease.title) !== null && _ref5 !== void 0 ? _ref5 : '',
    hasPassed: globalPercent >= passingPercentage
  };
};

var formatTestDate = function formatTestDate(value, locale) {
  if (!value) {
    return '—';
  }

  var timestamp = Number.parseInt(value, 10);

  if (Number.isNaN(timestamp)) {
    return '—';
  }

  return new Intl.DateTimeFormat(locale, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(new Date(timestamp));
};

var renderHeaderSkeleton = function renderHeaderSkeleton() {
  return /*#__PURE__*/_jsxs(UnifiedCard, {
    children: [/*#__PURE__*/_jsx(AssessmentSection, {
      children: /*#__PURE__*/_jsx(Skeleton, {
        active: true,
        avatar: true,
        paragraph: {
          rows: 2
        }
      })
    }), /*#__PURE__*/_jsx(SectionDivider, {}), /*#__PURE__*/_jsx(UserSection, {
      children: /*#__PURE__*/_jsx(Skeleton, {
        active: true,
        avatar: true,
        paragraph: {
          rows: 1
        }
      })
    }), /*#__PURE__*/_jsx(FeedbackSection, {
      children: /*#__PURE__*/_jsx(Skeleton, {
        active: true,
        paragraph: {
          rows: 2
        }
      })
    })]
  });
};

var SubmissionResultHeader = function SubmissionResultHeader(_ref6) {
  var _attendee$firstName, _attendee$lastName;

  var attendee = _ref6.attendee,
      labels = _ref6.labels,
      locale = _ref6.locale,
      metrics = _ref6.metrics,
      submission = _ref6.submission,
      feedbackContent = _ref6.feedbackContent,
      commentEditor = _ref6.commentEditor;

  var _useState = useState(false),
      isEditingComment = _useState[0],
      setEditingComment = _useState[1];

  var commentLabel = {
    noContent: labels.noComment,
    seeMore: labels.seeMore,
    seeLess: labels.seeLess
  };
  var initials = "".concat((((_attendee$firstName = attendee.firstName) === null || _attendee$firstName === void 0 ? void 0 : _attendee$firstName[0]) || '').toUpperCase()).concat((((_attendee$lastName = attendee.lastName) === null || _attendee$lastName === void 0 ? void 0 : _attendee$lastName[0]) || '').toUpperCase());
  return /*#__PURE__*/_jsxs(UnifiedCard, {
    children: [/*#__PURE__*/_jsxs(AssessmentSection, {
      children: [/*#__PURE__*/_jsxs(ScoreRingWrapper, {
        children: [/*#__PURE__*/_jsxs(ProgressRingHost, {
          children: [/*#__PURE__*/_jsx(AccuracyProgress, {
            type: "circle",
            percent: metrics.globalPercent,
            strokeColor: metrics.hasPassed ? '#2563eb' : '#dc2626',
            trailColor: "#e2e8f0",
            strokeWidth: 8
          }), /*#__PURE__*/_jsx(ProgressCenter, {
            children: /*#__PURE__*/_jsxs(ProgressCenterValue, {
              $hasPassed: metrics.hasPassed,
              children: [metrics.globalPercent, "%"]
            })
          })]
        }), /*#__PURE__*/_jsx(GlobalLabel, {
          children: labels.global
        })]
      }), /*#__PURE__*/_jsxs(AssessmentTextBlock, {
        children: [/*#__PURE__*/_jsx(AssessmentTitle, {
          children: labels.assessmentSummary
        }), /*#__PURE__*/_jsx(AssessmentSubtitle, {
          children: metrics.assignmentTitle
        })]
      }), /*#__PURE__*/_jsxs(StatBadges, {
        children: [/*#__PURE__*/_jsxs(StatBadge, {
          $variant: "score",
          children: [/*#__PURE__*/_jsxs(StatValue, {
            $variant: "score",
            children: [metrics.earnedScore, "/", metrics.totalScore]
          }), /*#__PURE__*/_jsx(StatLabel, {
            $variant: "score",
            children: labels.score
          })]
        }), /*#__PURE__*/_jsxs(StatBadge, {
          $variant: "time",
          children: [/*#__PURE__*/_jsxs(StatValue, {
            $variant: "time",
            children: [metrics.timeTakenMinutes, "m"]
          }), /*#__PURE__*/_jsx(StatLabel, {
            $variant: "time",
            children: labels.time
          })]
        })]
      })]
    }), /*#__PURE__*/_jsx(SectionDivider, {}), /*#__PURE__*/_jsxs(UserSection, {
      children: [/*#__PURE__*/_jsx(AvatarCircle, {
        children: initials
      }), /*#__PURE__*/_jsxs(UserDetails, {
        children: [/*#__PURE__*/_jsxs(UserName, {
          children: [attendee.firstName, " ", attendee.lastName]
        }), /*#__PURE__*/_jsx(UserEmail, {
          children: attendee.email
        })]
      }), /*#__PURE__*/_jsxs(TestDateBlock, {
        children: [/*#__PURE__*/_jsx(TestDateLabel, {
          children: labels.testDate
        }), /*#__PURE__*/_jsxs(TestDateValue, {
          children: [/*#__PURE__*/_jsx(CalendarOutlined, {}), formatTestDate(submission.submitTime, locale)]
        })]
      })]
    }), /*#__PURE__*/_jsxs(FeedbackSection, {
      children: [/*#__PURE__*/_jsxs(FeedbackHeader, {
        children: [/*#__PURE__*/_jsx(FeedbackIcon, {}), labels.teacherComment, commentEditor && !isEditingComment && /*#__PURE__*/_jsx(TextLinkButton, {
          onClick: function onClick() {
            return setEditingComment(true);
          },
          children: commentEditor.edit
        })]
      }), /*#__PURE__*/_jsx(FeedbackBody, {
        children: isEditingComment && commentEditor ? /*#__PURE__*/_jsx(CommentBox, {
          label: commentEditor.label,
          value: submission.comment,
          loading: commentEditor.isSaving,
          onSave: function onSave(value) {
            commentEditor.onSave(value);
            setEditingComment(false);
          },
          onCancel: function onCancel() {
            return setEditingComment(false);
          }
        }) : feedbackContent !== null && feedbackContent !== void 0 ? feedbackContent : /*#__PURE__*/_jsx(CommentPreview, {
          value: submission.comment,
          label: commentLabel
        })
      })]
    })]
  });
};

export var PracticeSubmissionResultPreview = function PracticeSubmissionResultPreview(_ref7) {
  var assignment = _ref7.assignment,
      submission = _ref7.submission,
      attendee = _ref7.attendee,
      formRelease = _ref7.formRelease,
      questionReleases = _ref7.questionReleases,
      answers = _ref7.answers,
      labels = _ref7.labels,
      locale = _ref7.locale,
      isLoading = _ref7.isLoading,
      renderSectionQuestions = _ref7.renderSectionQuestions,
      feedbackContent = _ref7.feedbackContent,
      commentEditor = _ref7.commentEditor;
  var scrollRef = useRef(null);

  var _useState2 = useState(0),
      selectedSectionIndex = _useState2[0],
      setSelectedSectionIndex = _useState2[1];

  var metrics = useMemo(function () {
    return getMetrics({
      assignment: assignment,
      submission: submission,
      attendee: attendee,
      formRelease: formRelease,
      questionReleases: questionReleases,
      answers: answers,
      labels: labels,
      locale: locale,
      isLoading: isLoading,
      renderSectionQuestions: renderSectionQuestions,
      feedbackContent: feedbackContent,
      commentEditor: commentEditor
    });
  }, [assignment, submission, attendee, formRelease, questionReleases, answers, labels, locale, isLoading, renderSectionQuestions, feedbackContent, commentEditor]);
  useEffect(function () {
    if (formRelease && selectedSectionIndex >= formRelease.sections.length) {
      setSelectedSectionIndex(0);
    }
  }, [formRelease, selectedSectionIndex]);
  var scrollToTop = useCallback(function () {
    var _scrollRef$current;

    (_scrollRef$current = scrollRef.current) === null || _scrollRef$current === void 0 ? void 0 : _scrollRef$current.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);
  var section = formRelease === null || formRelease === void 0 ? void 0 : formRelease.sections[selectedSectionIndex];
  var isReady = !isLoading && Boolean(assignment && submission && attendee && formRelease && questionReleases && answers);
  return /*#__PURE__*/_jsx(AudioProvider, {
    children: /*#__PURE__*/_jsx(SubmissionPreviewLayout, {
      ref: scrollRef,
      children: /*#__PURE__*/_jsxs(Content, {
        direction: "vertical",
        children: [!isReady || !metrics || !attendee || !submission ? renderHeaderSkeleton() : /*#__PURE__*/_jsx(SubmissionResultHeader, {
          attendee: attendee,
          labels: labels,
          locale: locale,
          metrics: metrics,
          submission: submission,
          feedbackContent: feedbackContent,
          commentEditor: commentEditor
        }), !isReady || !section ? /*#__PURE__*/_jsx(Skeleton, {
          active: true,
          paragraph: {
            rows: 8
          }
        }) : /*#__PURE__*/_jsxs(StyledSectionContainer, {
          children: [/*#__PURE__*/_jsx(SectionV2Preview, {
            section: section,
            sectionLabel: labels.section,
            readonly: true,
            disableAudioRules: true,
            allowMultipleAudiosPlay: true,
            audioPlayerLabels: labels.audioPlayer
          }), questionReleases.filter(function (question) {
            return question.sectionId === section.id.toString();
          }).length ? renderSectionQuestions(section.id.toString()) : /*#__PURE__*/_jsxs("i", {
            children: ["(", labels.noQuestions, ")"]
          })]
        }), isReady && formRelease && formRelease.sections.length > 1 && /*#__PURE__*/_jsxs(StyledButtonGroup, {
          children: [/*#__PURE__*/_jsx(SecondaryButton, {
            disabled: selectedSectionIndex === 0,
            onClick: function onClick() {
              setSelectedSectionIndex(function (index) {
                return index - 1;
              });
              scrollToTop();
            },
            children: labels.previousSection
          }), /*#__PURE__*/_jsx(PrimaryButton, {
            disabled: selectedSectionIndex === formRelease.sections.length - 1,
            onClick: function onClick() {
              setSelectedSectionIndex(function (index) {
                return index + 1;
              });
              scrollToTop();
            },
            children: labels.nextSection
          })]
        })]
      })
    })
  });
};